import {
	type QueryKey,
	type UseQueryOptions,
	useQuery,
} from "@tanstack/react-query";
import { HttpStatusCode } from "axios";
import { EOSApiError } from "../../clients/eos/exceptions/EOSApiError";
import { withErrorRecovery } from "../../services/common/higher-order-functions/with-error-recovery";
import useVehicleHealthService from "../../services/vehicle-health";
import type { GetVehicleSafetyCampaignsRawResponse } from "../../services/vehicle-health/validation/GetVehicleSafetyCampaigns/index.types";

const useRawVehicleSafetyCampaignsByVin = <
	TQueryFnData = GetVehicleSafetyCampaignsRawResponse | undefined,
	TError = Error,
	TData = TQueryFnData,
	TQueryKey extends QueryKey = (
		| "/v2/vehicle/safety-campaigns"
		| string
		| undefined
	)[],
>(
	vin?: string,
	options?: Omit<
		UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
		"queryKey" | "queryFn"
	>,
) => {
	const service = useVehicleHealthService();

	const query = useQuery<TQueryFnData, TError, TData, TQueryKey>({
		enabled: !!vin,
		queryKey: ["safety-campaigns-by-vin", vin] as unknown as TQueryKey,
		queryFn: withErrorRecovery(
			async () => {
				if (!vin) return undefined as TQueryFnData;
				const { data } = await service.findSafetyCampaigns(vin);
				return data as TQueryFnData;
			},
			(error) => {
				if (!(error instanceof EOSApiError)) throw error;
				if (error.status !== HttpStatusCode.NotFound) throw error;
				// B002 is a known EOS heuristic for no results
				if (error.code !== "B002") throw error;
				return {
					body: { data: { vehicleCampaignList: [] } },
				} as TQueryFnData;
			},
		),
		...options,
	});

	return {
		...query,
		isLoading: query.isLoading && query.fetchStatus !== "idle",
	};
};

export default useRawVehicleSafetyCampaignsByVin;
